import { EditOutlined } from "@ant-design/icons";
import {
  ImageNotSupportedOutlined,
  QrCode2Outlined,
} from "@mui/icons-material";
import { useMutation } from "@tanstack/react-query";
import { Button as AntButton, Space, Table, Tooltip } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import GenericTableContainer from "~/components/GenericTableContainer";
import { dateTimeFormat, roles } from "~/config";
import useAuthorization from "~/hooks/useAuthorization";
import useColumnSearch from "~/hooks/useColumnSearch";
import AuthorAvatar from "~/pages/components/AuthorAvatar";
import { getPlantationList } from "~/services/plantation.service";
import { getImageUrl, getTableParams } from "~/utils/utils";

const PlantationDetails = () => {
  const navigate = useNavigate();
  const { getColumnSearchProps, setSearchText, setSearchedColumn } =
    useColumnSearch();
  const { userHasRequiredRole } = useAuthorization([roles.ADMIN]);

  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 25,
    },
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);

    mutate(
      getTableParams({
        pagination: tableParams.pagination,
        filters: selectedKeys[0],
      })
    );
  };

  const columns = [
    {
      title: "Plant Image",
      key: "plant_mstr.Plant_Img",
      dataIndex: "plant_mstr.Plant_Img",
      responsive: ["lg", "md"],
      width: 150,
      render: (imageUrl, record) => {
        return (
          <AuthorAvatar
            image={getImageUrl(imageUrl)}
            icon={imageUrl === null ? <ImageNotSupportedOutlined /> : null}
          />
        );
      },
      fixed: "left",
      // ...getColumnSearchProps('Plant_Eng_Name', handleSearch, 'Plant Name'),
    },
    {
      title: "Plantation Name",
      dataIndex: "plant_mstr.Plant_Eng_Name",
      key: "plant_mstr.Plant_Eng_Name",
      fixed: "left",
      ...getColumnSearchProps(
        `plant_mstr.Plant_Eng_Name`,
        handleSearch,
        "Plant Name"
      ),
      ellipsis: { showTitle: false },

      render: (value) => (
        <Tooltip placement="leftTop" title={value}>
          {value}
        </Tooltip>
      ),
      width: 250,
    },
    {
      title: "Plotting Name",
      dataIndex: "plot_mstr.Plot_Name",
      key: "plot_mstr.Plot_Name",
      responsive: ["lg", "md"],
    },
    {
      title: "Plantation Type",
      dataIndex: "Plant_Type",
      key: "Plant_Type",
      responsive: ["lg", "md"],
    },
    {
      title: "No. of Plant",
      dataIndex: "Total_Plant_Count",
      key: "Total_Plant_Count",
      responsive: ["lg", "md"],
    },
    {
      title: "Monitoring Number",
      dataIndex: "Total_Plant_Monitoring_Count",
      key: "Total_Plant_Monitoring_Count",
      responsive: ["lg", "md"],
    },
    // new changes - tag v3.0.1
    {
      title: "Aspect",
      dataIndex: "Aspect",
      key: "Aspect",
      responsive: ["lg", "md"],
    },
    {
      title: "Soil Texture",
      dataIndex: "Soil_Texture",
      key: "Soil_Texture",
      responsive: ["lg", "md"],
    },
    {
      title: "Created At",
      dataIndex: "Created_Dt",
      key: "Created_Dt",
      responsive: ["lg", "md"],

      render: (record) =>
        record === null ? "" : moment(record).format(dateTimeFormat),
    },
    {
      title: "Created By",
      dataIndex: "created_by.Login_Name",
      key: "created_by.Login_Name",
      responsive: ["lg", "md"],
    },
    // new changes - tag v3.0.1
    {
      title: "Modified At",
      dataIndex: "Modified_Dt",
      key: "Modified_Dt",
      responsive: ["lg", "md"],

      render: (record) =>
        record === null ? "" : moment(record).format(dateTimeFormat),
    },
    {
      title: "Modified By",
      dataIndex: "modified_by.Login_Name",
      key: "modified_by.Login_Name",
      responsive: ["lg", "md"],
    },
    ...(userHasRequiredRole ? [{
      title: "Action",
      dataIndex: "action",
      fixed: "right",
      width: 100,

      render: (rowData, record) => (
        <Space size="middle">
          {/* new changes - tag v3.0.1 */}
          <Tooltip title="Edit">
            <AntButton
              type="primary"
              shape="circle"
              icon={<EditOutlined />}
              onClick={() => {
                console.log(rowData, record);
                navigate(
                  `/plantation-master/update/${record[
                    "plant_mstr.Plant_Eng_Name"
                  ]
                    .trim()
                    .replace(/\s+/g, "-")}`,
                  {
                    state: record,
                  }
                );
              }}
            />
          </Tooltip>
          <Tooltip title="Generate QR">
            <AntButton
              type="primary"
              shape="circle"
              icon={<QrCode2Outlined fontSize="16px" />}
              onClick={() =>
                navigate(
                  `/plantation-master/qr-code/${record["plant_mstr.Plant_Eng_Name"]}`,
                  {
                    state: record,
                  }
                )
              }
            />
          </Tooltip>
        </Space>
      ),
    }] : []),
  ];

  const {
    data: plantationData,
    isLoading,
    mutate,
  } = useMutation({
    mutationKey: ["plantationData"],
    mutationFn: (payloads) => getPlantationList(payloads),
    onSuccess: (data) => {
      // Modify the data response here
      setTableParams({
        ...tableParams,
        pagination: {
          ...tableParams.pagination,
          total: data.count,
          // 200 is mock data, you should read it from server
          // total: data.totalCount,
        },
      });

      console.log("ON SUCCESS", data);
    },
  });

  const handleTableChange = (pagination, filters, sorter) => {
    mutate(getTableParams({ pagination }));
    setTableParams({
      pagination,
      filters,
      ...sorter,
    });
  };

  useEffect(() => {
    mutate(getTableParams(tableParams));
  }, []);

  return (
    <GenericTableContainer
      cardHeaderTitle="Plantation Master"
      navigateTo="/plantation-master/create"
      cardButtonText="Add Plantation"
    >
      <Table
        scroll={{ y: 350, x: 3000 }}
        columns={columns}
        dataSource={plantationData?.data}
        loading={isLoading}
        pagination={{
          ...tableParams.pagination,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
          showSizeChanger: true,
        }}
        onChange={handleTableChange}
      />
    </GenericTableContainer>
  );
};

export default PlantationDetails;
